import { DateTime } from "luxon";
import { cacheableData, workbookCache, worksheetCache } from "./cache";
import { ValidationError, handleError, serializeErrorForWarning } from "../../errors";
import { EstimateReport } from "../../interfaces/EstimateReport";
import { ProjectReport } from "../../interfaces/ProjectReport";
import { GCGRReport } from "../../interfaces/GCGRReport";
import { ContractingReport } from "../../interfaces/ContractingReport";
import { CostModelReport } from "../../interfaces/CostModelReport";
import Logger from "../../clients/sentry";
import { DATA_TABLE_FUNCTION_NAMES } from "./customFunctions";
import {
  columnShouldBeNumeric,
  columnShouldBeDate,
  getColumnLetter,
  getColumnIndexFromAddress,
  valuesAreCalendarDates,
} from "./columns";
import {
  DATA_SHEET_NAMES,
  LEGACY_ESTIMATE_SHEET,
  REPORT_SHEET_NAMES,
  WorkbookTable,
  templateSheetName,
} from "./tableNames";

export interface LoadDataOptions {
  sizeColumns?: boolean;
  dateOnly?: boolean;
  /** Date columns written in UTC, not the user's time zone, for stamps compared across users. */
  utcColumns?: string[];
  adminTable?: boolean;
  alwaysHide?: boolean;
  addLastUpdated?: boolean;
  rowBackgroundFills?: (string | null)[];
  sheetName?: string;
  startCell?: string;
}

export interface DataExistsOptions {
  columnName?: string;
  checkValues?: boolean; // Check if column has any non-empty values
  expectedValues?: any[]; // Check if column contains specific values
}

// This state management system initializes and maintains the visibility state of data worksheets
// by syncing between in-memory state and workbook cache, ensuring helper functions operate
// with consistent visibility preferences across the application

let showDataSheetsState: boolean | null = null;

// Getter function to access the current state of data worksheet visibility

export const getShowDataSheetsState = (): boolean => {
  return showDataSheetsState ?? false;
};

export const initializeShowDataSheetsState = async (): Promise<boolean> => {
  if (showDataSheetsState === null) {
    const hasVisible = await hasVisibleDataWorksheets();
    const saved = await workbookCache.get("showDataSheets");
    const result: boolean = hasVisible || (saved ?? false);
    showDataSheetsState = result;
    return result;
  }
  return showDataSheetsState;
};

// Update both the in-memory state and persist the preference to workbook cache

export const updateShowDataSheetsState = async (show: boolean) => {
  showDataSheetsState = show;
  await workbookCache.set({ showDataSheets: show });
};

export const dataExists = async (tableName: string, options?: DataExistsOptions): Promise<boolean> => {
  try {
    return await Excel.run(async (context) => {
      const table = context.workbook.tables.getItemOrNullObject(tableName);
      await context.sync();

      if (table.isNullObject) {
        return false;
      }

      table.load("rows/count");
      await context.sync();

      if (table.rows.count === 0) {
        return false;
      }

      // Fix for excel sometimes reporting 1 row when table is actually empty
      if (table.rows.count === 1) {
        const rows = table.rows;
        rows.load("items");
        await context.sync();

        if (table.rows.items.length === 0) {
          return false;
        }

        const firstRowRange = table.rows.items[0].getRange();
        firstRowRange.load("values");
        await context.sync();

        const firstRowValues = firstRowRange.values;
        const isEmptyRow = firstRowValues[0].every((cell: any) => cell === null || cell === "");

        if (isEmptyRow) {
          return false;
        }
      }

      // If column-specific checks are requested
      if (options?.columnName) {
        const column = table.columns.getItemOrNullObject(options.columnName);
        await context.sync();

        if (column.isNullObject) {
          return false;
        }

        const dataRange = column.getDataBodyRange();
        dataRange.load("values");
        await context.sync();

        const columnValues = dataRange.values.map((row) => row[0]);

        // Check if column has any non-empty values
        if (options.checkValues) {
          const hasValues = columnValues.some((value) => value !== null && value !== "" && value !== undefined);
          if (!hasValues) {
            return false;
          }
        }

        // Check if column contains specific expected values
        if (options.expectedValues && options.expectedValues.length > 0) {
          const hasExpectedValues = options.expectedValues.some((expectedValue) =>
            columnValues.includes(expectedValue)
          );
          if (!hasExpectedValues) {
            return false;
          }
        }
      }

      return true;
    });
  } catch (error) {
    console.error(`Error checking for existence of ${tableName} Table:`, error);
    return false;
  }
};

export const convertToExcelDate = (
  dateTimeString: string | number | null | undefined,
  userTimeZone: string = "local"
): string => {
  // Check if the dateTimeString is null or empty
  if (!dateTimeString) {
    return "";
  }

  // A numeric value is already an Excel date serial — e.g. a date-formatted cell read back from
  // another table via getTableData and re-written (as syncLegacyReportTable does for __Report's
  // updated_at). It has no ISO string to parse, so pass it straight through; the date-formatted
  // column renders the serial correctly. Without this, `.replace` throws and the write aborts,
  // leaving the table cleared-but-empty.
  if (typeof dateTimeString !== "string") {
    return String(dateTimeString);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateTimeString)) {
    return dateTimeString;
  }

  // Step 1: Remove the "+00" timezone and add "T" between date and time for ISO format
  let normalizedDateTimeString = dateTimeString.replace(/\+00$/, "");
  normalizedDateTimeString = normalizedDateTimeString.replace(" ", "T");

  // Step 2: If the string has no milliseconds, add .000
  normalizedDateTimeString = normalizedDateTimeString.replace(/(\d{2}:\d{2}:\d{2})$/, "$1.000");

  // Step 3: Parse the datetime string using Luxon, assuming it's UTC
  const dateTime = DateTime.fromISO(normalizedDateTimeString, { zone: "utc" });

  // If the date is invalid, return the original string
  if (!dateTime.isValid) {
    return dateTimeString;
  }

  // Step 4: Convert to user's timezone
  const userDateTime = dateTime.setZone(userTimeZone === "local" ? DateTime.local().zoneName : userTimeZone);

  // Step 5: Format the datetime to an Excel-compatible format
  const excelDateString = userDateTime.toFormat("yyyy-LL-dd HH:mm:ss");

  return excelDateString;
};

/**
 * The inverse of `convertToExcelDate`: epoch milliseconds for a date read back from a report
 * table, or NaN if it isn't one. Such a cell reads back as an Excel serial in the wall-clock time
 * it was written in, to the second; one shown as Text reads back as the string that was written.
 *
 * @param zone `"utc"` for a column written in UTC (`utcColumns`), `"local"` for one written in the
 * user's time zone.
 */
export const excelDateToEpochMs = (value: unknown, zone: "utc" | "local" = "utc"): number => {
  if (typeof value === "number") {
    const wallClock = DateTime.fromMillis(Math.round((value - 25569) * 86400) * 1000, { zone: "utc" });
    return wallClock.setZone(zone, { keepLocalTime: true }).toMillis();
  }
  if (typeof value !== "string" || !value) return NaN;
  const written = DateTime.fromFormat(value, "yyyy-LL-dd HH:mm:ss", { zone });
  if (written.isValid) return written.toMillis();
  const iso = DateTime.fromISO(value.replace(" ", "T").replace(/\+00$/, "Z"));
  return iso.isValid ? iso.toMillis() : NaN;
};

export const legacyCheck = async (tableName: WorkbookTable) => {
  if (tableName === "Estimate") {
    const sheetExists = await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItemOrNullObject("data");
      await context.sync();
      return !sheet.isNullObject;
    });

    const tableExists = await Excel.run(async (context) => {
      const table = context.workbook.tables.getItemOrNullObject("estimateData");
      await context.sync();
      return !table.isNullObject;
    });

    if (sheetExists && tableExists) {
      return { sheetName: LEGACY_ESTIMATE_SHEET, tableName: "estimateData" as WorkbookTable };
    }
  }

  return null;
};

// Sheets that hold report metadata rather than loadable data tables — never toggled,
// deleted, or shown alongside the data sheets

const isReportSheet = (sheetName: string): boolean => REPORT_SHEET_NAMES.has(sheetName);

export async function loadDataIntoSheet(
  rows: any,
  tableName: WorkbookTable,
  options: LoadDataOptions = {
    sizeColumns: true,
    adminTable: false,
    alwaysHide: false,
    dateOnly: false,
    addLastUpdated: false,
  }
) {
  const prefix = options.adminTable ? "admin_" : "__";
  let sheetName: string = options.sheetName ?? prefix + tableName;
  const startCell: string = options.startCell ?? "A1";
  const legacyNames = await legacyCheck(tableName);
  if (legacyNames) {
    sheetName = legacyNames.sheetName;
    tableName = legacyNames.tableName;
  }

  try {
    await Excel.run(async (context) => {
      let sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
      await context.sync();

      if (rows.length === 0) {
        if (!sheet.isNullObject) {
          let table = sheet.tables.getItemOrNullObject(tableName);
          await context.sync();

          if (!table.isNullObject) {
            // If the table exists, keep only one row and hide the sheet
            const tableRange = table.getDataBodyRange();
            tableRange.clear();
            tableRange.load("rowCount");
            await context.sync();

            if (tableRange.rowCount > 1) {
              const deleteRange = table.getRange().getOffsetRange(1, 0).getEntireRow();
              deleteRange.delete(Excel.DeleteShiftDirection.up);
              await context.sync();
            }

            sheet.visibility = Excel.SheetVisibility.hidden;
          }
        }
        return; // Exit early since there's no data to insert
      }
      const isNewSheet = sheet.isNullObject;
      if (isNewSheet) {
        sheet = context.workbook.worksheets.add(sheetName);
      }
      if (options.alwaysHide || (isReportSheet(sheetName) && isNewSheet)) {
        sheet.visibility = Excel.SheetVisibility.hidden;
      } else if (!isReportSheet(sheetName)) {
        sheet.visibility =
          showDataSheetsState || options.adminTable ? Excel.SheetVisibility.visible : Excel.SheetVisibility.hidden;
      }

      let table = sheet.tables.getItemOrNullObject(tableName);
      await context.sync();

      let existingHeaders: string[] = [];
      if (!table.isNullObject) {
        table.load("columns/items/name");
        table.autoFilter.load("isDataFiltered");
        await context.sync();
        existingHeaders = table.columns.items.map((column) => column.name);
      }

      if (
        options.addLastUpdated &&
        !rows.some((row: Record<string, unknown>) => row && typeof row === "object" && "last_updated" in row)
      ) {
        const dateTime = new Date().toISOString();
        rows.forEach((row: Record<string, unknown>) => {
          row["last_updated"] = dateTime;
        });
      }

      let headers: string[];
      ({ headers, rows } = foldHeadersIgnoringCase(rows, existingHeaders));

      let savedFilters: SavedColumnFilter[] = [];
      if (!table.isNullObject) {
        if (table.autoFilter.isDataFiltered) savedFilters = await takeTableFilters(table, context);
        const tableRange = table.getDataBodyRange();
        tableRange.clear(); // Clear all data

        // Remove columns that are not in the new headers
        for (let i = existingHeaders.length - 1; i >= 0; i--) {
          if (!headers.includes(existingHeaders[i])) {
            table.columns.getItem(existingHeaders[i]).delete();
          }
        }

        // Add new columns that are not in the existing headers
        for (const header of headers) {
          if (!existingHeaders.includes(header)) {
            table.columns.add(undefined, undefined, header);
          }
        }
        await context.sync();

        // Resize the table to only include the header row (initially)
        const minimalRange = sheet.getRange(
          `${startCell}:${getColumnLetter(headers.length - 1)}${parseInt(startCell.match(/\d+/)![0]) + 1}`
        );
        table.resize(minimalRange);
        await context.sync();
      } else {
        // Create a new table if it does not exist
        const headerRange = sheet.getRange(
          `${startCell}:${getColumnLetter(headers.length - 1)}${startCell.match(/\d+/)![0]}`
        );
        headerRange.values = [headers];
        table = sheet.tables.add(headerRange, true);
        table.name = tableName;
        await context.sync();
      }

      if (rows.length > 0) {
        table.load("columns/items/name");
        await context.sync();
        const columnNames = table.columns.items.map((header) => header.name);
        const isDateColumn = columnNames.map((name) => columnShouldBeDate(name));
        const startRow = parseInt(startCell.match(/\d+/)![0], 10) + 1;
        const dataStartCell = `${startCell.match(/[A-Z]+/)}${startRow}`;
        const dataEndCell = `${getColumnLetter(columnNames.length - 1)}${startRow + rows.length - 1}`;
        const dataRangeAddress = `${dataStartCell}:${dataEndCell}`;

        const colStart = getColumnIndexFromAddress(startCell);
        columnNames.forEach((name, colIndex) => {
          const columnDataRange = sheet.getRangeByIndexes(startRow - 1, colStart + colIndex, rows.length, 1);
          let numberFormat = "@";
          if (columnShouldBeNumeric(name)) {
            numberFormat = "0.00";
          } else if (isDateColumn[colIndex]) {
            const isCalendarDate =
              options.dateOnly || valuesAreCalendarDates(rows.map((row: Record<string, unknown>) => row[name]));
            numberFormat = isCalendarDate ? "m/d/yyyy" : "m/d/yyyy h:mm AM/PM";
          }
          columnDataRange.numberFormat = [[numberFormat]];
        });
        await context.sync();

        context.application.suspendScreenUpdatingUntilNextSync();
        const dataToInsert = Array.from({ length: rows.length }, (_, rowIndex) => {
          const row = rows[rowIndex];
          return columnNames.map((name, colIndex) => {
            let value = row[name];
            if (value && typeof value === "object") {
              value = JSON.stringify(value);
            }
            if (isDateColumn[colIndex] && !options.dateOnly) {
              return String(convertToExcelDate(value, options.utcColumns?.includes(name) ? "utc" : "local"));
            }
            return String(value ?? "");
          });
        });

        const dataRange = sheet.getRange(dataRangeAddress);
        dataRange.values = dataToInsert;
        await context.sync();

        const fills = options.rowBackgroundFills;
        if (fills && columnNames.length > 0) {
          if (fills.length !== rows.length) {
            Logger.warn("rowBackgroundFills length does not match rows; skipping row fills", {
              tableName,
              rowCount: rows.length,
              fillCount: fills.length,
            });
          } else {
            const firstDataRowZeroBased = startRow - 1;
            for (let i = 0; i < rows.length; i++) {
              const rowRange = sheet.getRangeByIndexes(firstDataRowZeroBased + i, colStart, 1, columnNames.length);
              if (fills[i]) {
                rowRange.format.fill.color = fills[i] as string;
              } else {
                rowRange.format.fill.clear();
              }
            }
            await context.sync();
          }
        }
      }

      if (savedFilters.length > 0) await reapplyTableFilters(table, savedFilters, headers, context, tableName);
      if (options.sizeColumns) await formatTableColumns(table, context);
    });
  } catch (error) {
    // EXCEPTION: Logger error allowed here — the failure is swallowed rather than returned, so no
    // caller receives it and nothing in the hooks layer can log it instead.
    const err = handleError(error, { tableName, options });
    Logger.error("Error loading data into sheet", err);
  }
}

const foldHeadersIgnoringCase = (
  rows: Record<string, unknown>[],
  existingHeaders: string[]
): { headers: string[]; rows: Record<string, unknown>[] } => {
  const spellings = new Map(existingHeaders.map((name) => [name.toLowerCase(), name]));
  const headers = new Set<string>();
  let needsFold = false;
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    for (const key of Object.keys(row)) {
      const lowerKey = key.toLowerCase();
      if (!spellings.has(lowerKey)) spellings.set(lowerKey, key);
      const spelling = spellings.get(lowerKey)!;
      headers.add(spelling);
      if (spelling !== key) needsFold = true;
    }
  }
  if (!needsFold) return { headers: Array.from(headers), rows };

  const foldedRows = rows.map((row) =>
    row && typeof row === "object"
      ? Object.fromEntries(Object.entries(row).map(([key, value]) => [spellings.get(key.toLowerCase())!, value]))
      : row
  );
  return { headers: Array.from(headers), rows: foldedRows };
};

interface SavedColumnFilter {
  columnName: string;
  criteria: Excel.FilterCriteria;
}

const takeTableFilters = async (table: Excel.Table, context: Excel.RequestContext): Promise<SavedColumnFilter[]> => {
  const filters = table.columns.items.map((column) => column.filter.load("criteria"));
  await context.sync();

  const saved = table.columns.items.flatMap((column, index) => {
    const criteria = filters[index].criteria;
    return criteria?.filterOn ? [{ columnName: column.name, criteria }] : [];
  });
  table.clearFilters();
  return saved;
};

const reapplyTableFilters = async (
  table: Excel.Table,
  saved: SavedColumnFilter[],
  headers: string[],
  context: Excel.RequestContext,
  tableName: string
) => {
  try {
    saved
      .filter(({ columnName }) => headers.includes(columnName))
      .forEach(({ columnName, criteria }) => table.columns.getItem(columnName).filter.apply(criteria));
    await context.sync();
  } catch (error) {
    Logger.warn("Could not reapply table filters after loading data; left the table unfiltered", {
      tableName,
      ...serializeErrorForWarning(error),
    });
    table.clearFilters();
    await context.sync().catch(() => undefined);
  }
};

const formatTableColumns = async (table: Excel.Table, context: Excel.RequestContext) => {
  const maxColumnWidth = 300; // Set maximum width for any column

  // Auto-fit all columns in the table
  table.columns.load("items");
  await context.sync();

  // Auto-fit each column
  table.columns.items.forEach((column) => {
    column.getRange().format.autofitColumns();
  });
  await context.sync();

  // Now iterate through columns and set max width to 300 pixels if necessary
  const columnRanges = table.columns.items.map((column) => column.getRange().load("format/columnWidth"));
  await context.sync();

  const tooWide = columnRanges.filter((columnRange) => columnRange.format.columnWidth > maxColumnWidth);
  if (tooWide.length > 0) {
    tooWide.forEach((columnRange) => {
      columnRange.format.columnWidth = maxColumnWidth;
    });
    await context.sync();
  }
};

export const getTableData = async (tableName: WorkbookTable): Promise<any[]> => {
  return Excel.run(async (context) => {
    try {
      const workbookTable = context.workbook.tables.getItemOrNullObject(tableName);
      await context.sync();
      if (workbookTable.isNullObject) {
        return [];
      }
      workbookTable.load("rows, columns");
      await context.sync();

      const headers = workbookTable.columns.items.map((col) => col.name);
      return (
        workbookTable.rows.items
          .map((row) => {
            const record: { [key: string]: any } = {};
            row.values[0].forEach((cellValue, index) => {
              record[headers[index]] = cellValue;
            });
            return record;
          })
          // Cleared tables keep one blank data row (Excel tables can't drop below one);
          // filter it out so callers' `length === 0` checks see the table as empty
          .filter((record) => Object.values(record).some((value) => value !== "" && value != null))
      );
    } catch (error) {
      console.error(`Error getting table data from ${tableName}:`, error);
      return [];
    }
  });
};

export const getEstimateReportData = async (): Promise<Partial<EstimateReport>[]> => {
  const data = await getTableData("EstimateReport");
  return (data.length > 0 ? data : await getTableData("Report")) as Partial<EstimateReport>[];
};

export const getProjectReportData = async (): Promise<Partial<ProjectReport>[]> => {
  const data = await getTableData("ProjectReport");
  return (data.length > 0 ? data : await getTableData("Report")) as Partial<ProjectReport>[];
};

export const getGCGRReportData = async (): Promise<Partial<GCGRReport>[]> => {
  const data = await getTableData("GCGRReport");
  if (data.length > 0) return data as Partial<GCGRReport>[];
  // Legacy workbooks (new code, no GCGRReport row yet): derive the single resolved-GCGR row from
  // the __GCGR data table, which carries id, project and the client last_updated stamp. Its
  // updated_at is left out: __GCGR stores dates without a time of day, too coarse to be the
  // staleness baseline, so the row reads as stale until a refresh writes a GCGRReport row.
  const legacy = await getTableData("GCGR");
  if (legacy.length === 0) return [];
  const g = legacy[0];
  return [{ gcgr_id: g.id, project_id: g.project, last_updated: g.last_updated }] as Partial<GCGRReport>[];
};

export const getContractingReportData = async (): Promise<Partial<ContractingReport>[]> => {
  // No legacy fallback: contracting is new on this branch; pre-migration workbooks legitimately
  // have no contracting data, so an empty result (staleness skips contracting) matches main.
  return (await getTableData("ContractingReport")) as Partial<ContractingReport>[];
};

export const getCostModelReportData = async (): Promise<Partial<CostModelReport>[]> => {
  // No legacy fallback: no workbook predates the table.
  return (await getTableData("CostModelReport")) as Partial<CostModelReport>[];
};

export const getNamedRangeFromWorksheet = async (
  worksheet: Excel.Worksheet,
  namedRangeName: string
): Promise<Excel.Range | null> => {
  try {
    const namedRange = worksheet.getRange(namedRangeName);
    namedRange.load("address");
    await worksheet.context.sync();
    return namedRange; // Return the range if it exists and is valid
  } catch (error) {
    return null; // Return null if the range does not exist or is invalid
  }
};

/**
 * Returns all named ranges on the worksheet whose names start with the given prefix.
 * Uses worksheet.names to enumerate, then filters by prefix.
 */

export const getNamedRangesByPrefixFromWorksheet = async (
  worksheet: Excel.Worksheet,
  prefix: string
): Promise<Excel.Range[]> => {
  const names = worksheet.names;
  names.load("items/name");
  await worksheet.context.sync();

  const matching = names.items.filter((n) => n.name.startsWith(prefix));
  const ranges = matching.map((n) => {
    const range = n.getRange();
    range.load("rowIndex");
    return range;
  });

  if (ranges.length > 0) await worksheet.context.sync();
  return ranges;
};

export const safeWorksheetName = async (
  workbook: Excel.Workbook,
  baseName: string,
  appendCounterSuffix = true
): Promise<string> => {
  const maxLength = 31; // Max length for worksheet names in Excel
  const invalidChars = /[\\/:*?"<>|]/g; // Invalid characters for worksheet names

  let sanitizedBase = baseName.replace(invalidChars, "").slice(0, maxLength);

  if (!sanitizedBase) {
    throw new ValidationError("Worksheet name is empty after sanitization.");
  }

  let uniqueName = sanitizedBase;
  let counter = 1;

  while (true) {
    const sheet = workbook.worksheets.getItemOrNullObject(uniqueName);
    sheet.load("isNullObject");
    await workbook.context.sync();

    if (sheet.isNullObject) {
      break; // The name is unique, exit the loop
    }

    if (!appendCounterSuffix) {
      // If we are not appending a counter suffix, just return the sanitized base name
      uniqueName = sanitizedBase;
      break;
    }

    const suffix = `_${counter}`;
    const maxBaseLength = maxLength - suffix.length;
    sanitizedBase = sanitizedBase.slice(0, maxBaseLength); // Truncate base if suffix overflows
    uniqueName = `${sanitizedBase}${suffix}`;
    counter++;
  }

  return uniqueName;
};

export async function setCalculationManual() {
  await Excel.run(async (context) => {
    const application = context.workbook.application;
    application.calculationMode = Excel.CalculationMode.manual;
    await context.sync();
    console.log("CALCULATION mode set to MANUAL");
  });
}

export async function setCalculationAutomatic() {
  await Excel.run(async (context) => {
    const application = context.workbook.application;
    application.calculationMode = Excel.CalculationMode.automatic;
    await context.sync();
    console.log("CALCULATION mode set to AUTOMATIC");
  });
}

export async function setPrintAreaToUsedRange(worksheet: Excel.Worksheet, modifyLastColumnWidth = 0) {
  let usedRange = worksheet.getUsedRange();

  if (modifyLastColumnWidth) {
    usedRange.load("columnCount");
    await worksheet.context.sync();
    const lastColumn = usedRange.getColumn(usedRange.columnCount - 1);
    lastColumn.format.columnWidth = modifyLastColumnWidth;
  }

  await worksheet.context.sync();
  worksheet.pageLayout.setPrintArea(usedRange);
  await worksheet.context.sync();
}

export async function toggleDataWorksheets(show: boolean) {
  await Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();

    for (const worksheet of worksheets.items) {
      if (worksheet.name.startsWith("__") && !worksheet.name.startsWith("___") && !isReportSheet(worksheet.name)) {
        worksheet.visibility = show ? Excel.SheetVisibility.visible : Excel.SheetVisibility.hidden;
      }
    }
    await context.sync();
  });
}

export async function deleteDataWorksheets(): Promise<void> {
  await Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    const workbookNames = context.workbook.names;
    workbookNames.load("items/name");
    await context.sync();

    for (const worksheet of worksheets.items) {
      if (worksheet.name.startsWith("__") && !worksheet.name.startsWith("___") && !isReportSheet(worksheet.name)) {
        worksheet.delete();
      }
    }

    for (const name of workbookNames.items) {
      if (DATA_TABLE_FUNCTION_NAMES.includes(name.name)) {
        name.delete();
      }
    }

    await context.sync();
  });
}

export const hasModernDataTableWorksheets = async (): Promise<boolean> => {
  return await Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();
    return worksheets.items.some((ws) => DATA_SHEET_NAMES.has(ws.name));
  });
};

export const hasDataTableWorksheets = async (): Promise<boolean> => {
  if (await hasModernDataTableWorksheets()) return true;
  // Legacy workbooks keep estimate data in sheet "data" / table "estimateData"; they must
  // count as having data sheets so refresh paths keep writing into the legacy table
  return (await legacyCheck("Estimate")) !== null;
};

export const hasVisibleDataWorksheets = async (): Promise<boolean> => {
  return await Excel.run(async (context) => {
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name,items/visibility");
    await context.sync();

    return worksheets.items.some(
      (worksheet) =>
        worksheet.name.startsWith("__") &&
        !isReportSheet(worksheet.name) &&
        worksheet.visibility === Excel.SheetVisibility.visible
    );
  });
};

export const getRangeFromString = async (
  context: Excel.RequestContext,
  addressString: string
): Promise<Excel.Range | undefined> => {
  const parts = addressString.split("!");
  if (parts.length !== 2) {
    console.warn(`Invalid address string: "${addressString}"`);
    return;
  }

  const sheetName = parts[0].replace(/^'|'$/g, "");
  const rangeAddr = parts[1];
  const sheet = context.workbook.worksheets.getItemOrNullObject(sheetName);
  await context.sync();
  if (sheet.isNullObject) {
    console.warn(`Worksheet not found: "${sheetName}"`);
    return;
  }

  const range = sheet.getRange(rangeAddr);
  await context.sync();
  if (range.isNullObject) {
    console.warn(`Range not found or invalid: "${rangeAddr}" on sheet "${sheetName}"`);
    return;
  }

  return range;
};

export const clearRange = async (params: {
  range: Excel.Range;
  preserveFormulas: boolean;
  /** Column offsets within `range` whose formulas are cleared even when preserving. */
  clearFormulasInColumns?: Set<number>;
}) => {
  const { range, preserveFormulas, clearFormulasInColumns } = params;
  if (!preserveFormulas) {
    range.clear(Excel.ClearApplyTo.contents);
    await range.context.sync();
    return;
  }

  range.load("formulas");
  await range.context.sync();

  const formulas = (range.formulas as (string | null)[][]) || [];
  const rowCount = formulas.length;
  const colCount = formulas[0]?.length ?? 0;

  for (let r = 0; r < rowCount; r++) {
    for (let c = 0; c < colCount; c++) {
      const f = formulas[r][c];
      const isFormula = !!f && f.toString().startsWith("=");
      if (!isFormula || clearFormulasInColumns?.has(c)) {
        range.getCell(r, c).clear(Excel.ClearApplyTo.contents);
      }
    }
  }

  await range.context.sync();
};

/**
 * Return true if a and b contain exactly the same elements (no duplicates),
 * regardless of order.
 */

/** The sheet the user is looking at. Used for labelling actions that run against it. */
export const getActiveWorksheetName = async (): Promise<string> =>
  Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    sheet.load("name");
    await context.sync();
    return sheet.name;
  });

/** Persists one report option against a worksheet. Template sheets store their own. */
export const setWorksheetOption = async (
  worksheetName: string,
  key: string,
  value: cacheableData[string],
  isTemplate = false
): Promise<void> =>
  Excel.run(async (context) => {
    const sheetName = isTemplate ? templateSheetName(worksheetName) : worksheetName;
    const worksheet = context.workbook.worksheets.getItem(sheetName);
    await context.sync();
    await worksheetCache.setOptions(worksheet, { [key]: value });
  });
